import { useContext, useId, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectEmpty, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { DateTextInput } from "@/components/DatePicker";
import { useFieldUndo } from "@/hooks/useFieldUndo";
import { SearchableSelect, SEARCHABLE_MIN_OPTIONS } from "@/components/SearchableSelect";
import { cn } from "@/lib/utils";
import type { V2Value, V2Bag } from "@/features/applications/api/applications.api";
import { dateOnly } from "@/utils/dates";
import { type FieldDef } from "./model";
import { maskFor, validateField } from "./rules";
import { ValidationContext, isEmptyVal, overMaxLength } from "./validation";
import { tooLong } from "@/lib/fieldLimits";
import { useReadOnly } from "./readonly";
import { YesNo, ReqBadge } from "./primitives";

export function FieldInput({
  def,
  value,
  bag,
  onChange,
  inlineLabel,
}: {
  def: FieldDef;
  value: V2Value;
  bag?: V2Bag;
  onChange: (value: V2Value) => void;
  inlineLabel?: string;
}) {
  const radioName = useId();
  const { missing, errors, markFilled, markMissing, markError, prefix } = useContext(ValidationContext);
  const readOnly = useReadOnly();
  const fullKey = prefix + def.key;
  const errorMsg = errors.get(fullKey) ?? null;
  const isInvalid = !readOnly && (missing.has(fullKey) || errorMsg !== null);
  const isRequired = def.required === true
    || (def.requiredWhen ? def.requiredWhen(bag ?? {}) : false)
    || (def.requiredHint ? def.requiredHint(bag ?? {}) : false);
  const mask = maskFor(def.rule);

  const change = (v: V2Value) => {
    onChange(v);
    const msg = def.check && !readOnly && !isEmptyVal(v) ? validateField(def, v, { ...(bag ?? {}), [def.key]: v }) : null;
    if (msg) markError(fullKey, msg); else markFilled(fullKey);
  };

  const undo = useFieldUndo<V2Value>(value, change);
  const edit = (v: V2Value) => {
    undo.record(v);
    change(v);
  };

  const validateNow = (value: V2Value) => {
    if (readOnly) return;
    const enforcedRequired = def.required === true || (def.requiredWhen ? def.requiredWhen(bag ?? {}) : false);
    if (enforcedRequired && isEmptyVal(value)) { markMissing(fullKey); return; }
    if (overMaxLength(def.maxLength, value)) { markError(fullKey, tooLong(def.label, def.maxLength!)); return; }
    const msg = validateField(def, value, bag ?? {});
    if (msg) markError(fullKey, msg); else markFilled(fullKey);
  };
  const runBlur = () => validateNow(value);

  if (def.type === "checkbox") {
    return (
      <Label className={cn("flex items-center gap-2 text-sm font-normal", !def.tight && "md:pt-5", readOnly ? "text-slate-500" : "text-slate-700")}>
        <Checkbox
          checked={value === true}
          disabled={readOnly}
          onCheckedChange={(c) => change(c === true)}
        />
        {def.label}
      </Label>
    );
  }

  const reqCls = isRequired ? "border-amber-300 bg-amber-50/70" : undefined;
  const fieldCls = isInvalid ? "border-destructive bg-destructive/5" : readOnly ? undefined : reqCls;
  const wide = def.type === "textarea" || def.type === "list" || def.type === "multicheck";

  const isRadio = def.type === "yesno" || (def.type === "select" && Boolean(def.radio));
  const labelEl = (
    <div className={cn("flex items-center gap-1.5", isRadio ? "mb-3" : "mb-1")}>
      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{def.label}</span>
      {isRequired && !readOnly && <ReqBadge />}
    </div>
  );
  const hintEl = def.hint ? <p className="text-xs text-slate-400 mt-1">{def.hint}</p> : null;

  let control: ReactNode;
  if (def.type === "yesno") {
    const locked = def.locked === true;
    control = <YesNo name={radioName} value={locked ? (def.lockedValue ?? true) : value} disabled={locked || readOnly} onChange={change} />;
  } else if (def.type === "multicheck") {
    const chosen = Array.isArray(value) ? value : [];
    const opts = (def.options ?? []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
    control = (
      <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
        {opts.map((o) => (
          <Label key={o.value} className={cn("flex items-center gap-2 text-sm font-normal", readOnly ? "text-slate-500" : "text-slate-700")}>
            <Checkbox
              checked={chosen.includes(o.value)}
              disabled={readOnly}
              onCheckedChange={(c) =>
                change(c === true ? [...chosen, o.value] : chosen.filter((v) => v !== o.value))
              }
            />
            {o.label}
          </Label>
        ))}
      </div>
    );
  } else if (def.type === "select") {
    const listed = (def.options ?? []).map((o) => (typeof o === "string" ? { value: o, label: o } : o));
    const current = typeof value === "string" ? value : "";
    const opts = !def.radio && current && !listed.some((o) => o.value === current)
      ? [...listed, { value: current, label: current }]
      : listed;
    control = def.radio ? (
      <RadioGroup
        className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-1"
        value={typeof value === "string" ? value : ""}
        disabled={readOnly}
        onValueChange={(v) => change(v)}
      >
        {opts.map((o) => (
          <Label key={o.value} htmlFor={`${radioName}-${o.value}`} className={cn("flex items-center gap-2 text-sm font-normal", readOnly ? "text-slate-500" : "text-slate-700")}>
            <RadioGroupItem id={`${radioName}-${o.value}`} value={o.value} />
            {o.label}
          </Label>
        ))}
      </RadioGroup>
    ) : opts.length >= SEARCHABLE_MIN_OPTIONS ? (
      <SearchableSelect
        value={typeof value === "string" ? value : ""}
        onValueChange={(v) => change(v)}
        options={opts}
        placeholder="— Select —"
        searchPlaceholder={`Search ${def.label.toLowerCase()}…`}
        disabled={readOnly}
        className={fieldCls}
      />
    ) : (
      <Select
        value={typeof value === "string" && value ? value : undefined}
        disabled={readOnly}
        onValueChange={(v) => change(v)}
      >
        <SelectTrigger className={fieldCls}>
          <SelectValue placeholder="— Select —" />
        </SelectTrigger>
        <SelectContent>
          {opts.length === 0 ? (
            <SelectEmpty />
          ) : (
            opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)
          )}
        </SelectContent>
      </Select>
    );
  } else if (def.type === "textarea") {
    control = (
      <div>
        {!readOnly && def.presets && def.presets.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {def.presets.map((p) => (
              <Button key={p} type="button" variant="outline" size="sm" onClick={() => change(p)}>
                {p}
              </Button>
            ))}
          </div>
        )}
        <Textarea
          className={fieldCls}
          rows={3}
          disabled={readOnly}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => edit(e.target.value || null)}
          onKeyDown={undo.onKeyDown}
          onBlur={runBlur}
        />
      </div>
    );
  } else if (def.type === "number") {
    control = (
      <Input
        type="number"
        min={0}
        max={def.max}
        step={def.integer ? 1 : undefined}
        inputMode={def.integer ? "numeric" : undefined}
        disabled={readOnly}
        className={fieldCls}
        placeholder={def.placeholder}
        value={typeof value === "number" ? value : ""}
        onChange={(e) => {
          if (e.target.value === "") {
            edit(null);
            return;
          }
          const n = Number(e.target.value);
          if (Number.isNaN(n) || n < 0) return;
          if (def.max !== undefined && n > def.max) return;
          if (def.integer && !Number.isInteger(n)) return;
          edit(n);
        }}
        onKeyDown={undo.onKeyDown}
        onBlur={runBlur}
      />
    );
  } else if (def.type === "date") {
    control = (
      <DateTextInput
        className={cn(fieldCls, readOnly && "bg-muted text-slate-600")}
        readOnly={readOnly}
        value={typeof value === "string" ? dateOnly(value) : ""}
        onChange={(v) => edit(v)}
        onKeyDown={undo.onKeyDown}
        onBlur={(committed) => validateNow(committed)}
        disableFuture={def.disableFuture}
        defaultYear={def.defaultYear}
      />
    );
  } else if (def.type === "list") {
    control = (
      <Input
        type="text"
        className={fieldCls}
        disabled={readOnly}
        value={Array.isArray(value) ? value.join(", ") : ""}
        onChange={(e) => edit(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
        onKeyDown={undo.onKeyDown}
        onBlur={runBlur}
      />
    );
  } else {
    control = (
      <Input
        type="text"
        className={fieldCls}
        disabled={readOnly}
        placeholder={def.placeholder}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => {
          const raw = e.target.value;
          const next = mask ? mask(raw) : raw;
          edit(next || null);
        }}
        onKeyDown={undo.onKeyDown}
        onBlur={runBlur}
      />
    );
  }

  if (inlineLabel) {
    return (
      <div data-missing={isInvalid ? "true" : undefined}>
        <Label className="flex items-center gap-2 text-xs font-medium text-slate-500">
          <span className="whitespace-nowrap">{inlineLabel}</span>
          <div className="w-20 [&_input]:h-8 [&_input]:px-2 [&_input]:text-sm">{control}</div>
        </Label>
        {isInvalid && <p className="mt-1 text-xs text-destructive">{errorMsg ?? "Required."}</p>}
      </div>
    );
  }

  return (
    <div className={cn(wide && "md:col-span-2 lg:col-span-3")} data-missing={isInvalid ? "true" : undefined}>
      {labelEl}
      {control}
      {isInvalid ? <p className="text-xs text-destructive mt-1">{errorMsg ?? "Required."}</p> : hintEl}
    </div>
  );
}
